import UseTranslationHook from "../../hooks/UseTranslationHook";
import { useEffect, useState } from "react";
import UsePaginationHook from "../../hooks/UsePaginationHook";
import {
  cancelFirmSubscription,
  FIRM_PURCHASED_SUBSCRIPTION_ADVANCE_SEARCH_INITIAL_VALUE,
  getAllFirmPurchasedSubscription,
  getFirmNameDdl,
  getFirmPurchasedSubscription,
  IFirmPurchasedSubscription,
  IFirmPurchasedSubscriptionSearch,
} from "./FirmPurchasedSubscriptionViewController";
import { IOption, IPagination } from "../../data/AppInterface";
import {
  deleteIcon,
  filterIcon,
  sendEmailIcon,
  smsIcon,
} from "../../assets/icons/SvgIconList";
import { ORDER_BY } from "../../utils/AppConstants";
import {
  AdvanceSearch,
  ICButton,
  ICConfirmationModal,
  ICTable,
} from "../../core-component/ui";
import { ICCheckbox, ICDropDown } from "../../core-component/form";
import { BIT_VALUE } from "../../utils/AppEnumerations";
import {
  generateYearRange,
  prepareMessageFromParams,
} from "../../utils/AppFunctions";
import FirmSubscriptionAssignmentForm from "./FirmSubscriptionAssignmentForm";

let timer: ReturnType<typeof setTimeout>;

const FirmPurchasedSubscriptionView = () => {
  const { t } = UseTranslationHook();
  const [isLoading, setIsLoading] = useState(true);
  const [enterpriseSearch, setEnterpriseSearch] = useState("");
  const { pagination, setChangePagination } = UsePaginationHook();
  const [firmPurchasedSubscriptionList, setFirmPurchasedSubscriptionList] =
    useState<IFirmPurchasedSubscription[]>([]);
  const [firmName, setFirmName] = useState<IOption<string>[]>([]);
  const [showAssignmentForm, setShowAssignmentForm] = useState(false);
  const [subscriptionToCancel, setSubscriptionToCancel] =
    useState<IFirmPurchasedSubscription>();
  const [advanceSearch, setAdvanceSearch] =
    useState<IFirmPurchasedSubscriptionSearch>(
      FIRM_PURCHASED_SUBSCRIPTION_ADVANCE_SEARCH_INITIAL_VALUE
    );
  const yearOptions = generateYearRange(1951, 2100);

  useEffect(() => {
    fetchFirmPurchasedSubscription({ ...pagination, currentPage: 1 });
  }, [enterpriseSearch, advanceSearch]);

  useEffect(() => {
    fetchFirmName();
  }, []);

  const fetchFirmPurchasedSubscription = (paginationPlayLoad: IPagination) => {
    setIsLoading(true);
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(async () => {
      await getAllFirmPurchasedSubscription(
        setFirmPurchasedSubscriptionList,
        paginationPlayLoad,
        setChangePagination,
        enterpriseSearch,
        advanceSearch
      );
      setIsLoading(false);
    }, 500);
  };
  const fetchFirmName = async () => {
    await getFirmNameDdl(setFirmName);
  };

  const handleCancelSubscription = async (value: boolean) => {
    if (value && subscriptionToCancel) {
      const cancelled = await cancelFirmSubscription(subscriptionToCancel.id);
      if (cancelled) {
        fetchFirmPurchasedSubscription(pagination);
      }
    }
    setSubscriptionToCancel(undefined);
  };

  const handleCloseAssignmentForm = (status: boolean) => {
    setShowAssignmentForm(false);
    if (status) {
      fetchFirmPurchasedSubscription(pagination);
    }
  };

  const customActionView = (item: IFirmPurchasedSubscription) => {
    return (
      <div className="flex item-center justify-center w-full">
        <div
          className="size-5 cursor-pointer"
          title={t("send_mail_tooltip_Icon")}
          onClick={() => {}}
        >
          {sendEmailIcon}
        </div>
        <div
          className="ml-2 size-5 cursor-pointer"
          title={t("send_sms_tooltip_Icon")}
          onClick={() => {}}
        >
          {smsIcon}
        </div>
        {Number(item.remaining_expiry_days) > 0 && (
          <div
            className="ml-2 size-5 cursor-pointer"
            title={t("cancel_subscription_tooltip_icon")}
            onClick={() => setSubscriptionToCancel(item)}
          >
            {deleteIcon}
          </div>
        )}
      </div>
    );
  };
  const filterView = (
    <div className="w-full  grid grid-cols-4 gap-4 mt-4">
      <ICDropDown
        className="min-w-72"
        placeholder={t("firm_name")}
        selected={advanceSearch.firmId ? advanceSearch.firmId : undefined}
        options={firmName}
        searchable
        onSelect={(option) =>
          setAdvanceSearch((prev) => ({
            ...prev,
            firmId: option.data_value,
          }))
        }
      />
      <ICDropDown
        className="min-w-72"
        placeholder={t("select_year")}
        selected={advanceSearch.year ? advanceSearch.year : undefined}
        options={yearOptions}
        searchable
        onSelect={(option) =>
          setAdvanceSearch((prev) => ({
            ...prev,
            year: option.data_value,
          }))
        }
      />
      <ICCheckbox
        label={t("show_historical_records")}
        checkboxClassName="ml-4"
        checked={advanceSearch.showHistoricalRecords === BIT_VALUE.One}
        onChange={(option) =>
          setAdvanceSearch((prev) => ({
            ...prev,
            showHistoricalRecords: option ? BIT_VALUE.One : BIT_VALUE.Zero,
          }))
        }
      />
      <div className="!py-0 ml-4">
        <ICButton
          onClick={() =>
            setAdvanceSearch(
              FIRM_PURCHASED_SUBSCRIPTION_ADVANCE_SEARCH_INITIAL_VALUE
            )
          }
          className="flex !w-fit"
        >
          <div className="size-5 grid place-self-center">{filterIcon}</div>
          &nbsp;{t("clear")}
        </ICButton>
      </div>
    </div>
  );
  return (
    <div className="w-full overflow-scroll scrollbar-remove p-4">
      <AdvanceSearch
        handleOnClickAdd={() => setShowAssignmentForm(true)}
        handleSearchChange={(text) => {
          setEnterpriseSearch(text);
        }}
        searchText={enterpriseSearch}
        filterNode={filterView}
        refreshTable={() => {
          fetchFirmPurchasedSubscription(pagination);
        }}
      />

      <ICTable<IFirmPurchasedSubscription>
        isLoading={isLoading}
        totalFlex="grid grid-cols-11"
        fields={getFirmPurchasedSubscription(customActionView, t)}
        data={firmPurchasedSubscriptionList}
        pagination={pagination}
        handleChangePerPageRows={(perPageRow) =>
          fetchFirmPurchasedSubscription({
            ...pagination,
            perPageRows: perPageRow,
            currentPage: 1,
          })
        }
        handleChangePage={(page) =>
          fetchFirmPurchasedSubscription({ ...pagination, currentPage: page })
        }
        handleSort={(field) =>
          fetchFirmPurchasedSubscription({
            ...pagination,
            sortBy: field.key,
            orderBy:
              pagination.orderBy === ORDER_BY.desc
                ? ORDER_BY.asc
                : ORDER_BY.desc,
          })
        }
      />

      {showAssignmentForm && (
        <FirmSubscriptionAssignmentForm
          handleClose={handleCloseAssignmentForm}
        />
      )}
      {subscriptionToCancel && (
        <ICConfirmationModal
          title={t("cancel_subscription")}
          message={prepareMessageFromParams(t("cancel_subscription_message"), [
            ["packageName", subscriptionToCancel.subscription_package_name],
            ["firmName", subscriptionToCancel.firm_name],
          ])}
          onClose={(value) => handleCancelSubscription(value)}
        />
      )}
    </div>
  );
};
export default FirmPurchasedSubscriptionView;
